import { withAnalytic } from '@skooldio/analytics-provider';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import { compose } from 'recompose';
import styled, { css, withTheme } from 'styled-components';
import { display } from 'styled-system';
import urljoin from 'url-join';
import log from 'loglevel';

import { FCMPlugin } from 'Util/Capacitors/SkooldioCapacitor';
import { nsUnregisterFcmToken } from 'GraphQL/mutation/FCMToken.mutation';
import { AppFeedbackReport } from 'Containers/Feedback';
import { withConfig } from 'Context/ConfigContext';
import { CapacitorUtils } from 'Util/Capacitors';
import { getAppUIVariantConfig } from 'Util/uiVariantSet';
import GoToGuidedPracticeButton from 'Containers/GoToGuidedPracticeButton';

import { getStoreApolloClient } from '../../GraphQL';
import UserButton from '../../Containers/UserButton';
import { genUserEvent, USER_ACTIONS } from '../../Util/Analytics';
import { withCapacitor } from '../../Util/Capacitors/hoc';
import { getClassroomProviderData } from '../../Util/classroomProviderUtils';
import fetch2, { METHOD } from '../../Util/fetch';
import { Box, Flex, SvgIcon, Text } from '../Base';
import Notification from '../Notification';
import QRScanner from '../QRScanner';
import SearchMyCourse from '../SearchMyCourse';

import MenuContainer from './MenuContainer';

const NavBox = styled(Flex)`
  ${display};
`;

const NavItem = styled(({ is, ...props }) =>
  is === 'a' ? <a {...props} /> : <NavLink {...props} />
)`
  height: 100%;
  display: flex;
  align-items: center;
  color: ${({ theme: colors }) => colors.white};
  border-bottom: ${({ theme: colors }) => `3px solid ${colors.transparent}`};
  &:hover {
    color: ${({ theme: colors }) => colors.greyscale.grey3};
  }
`;

const BoxSearch = styled(Box)`
  ${({ theme, hasQRScan }) => css`
    position: absolute;
    right: ${hasQRScan ? theme.space.APP_PADDING_X + 86 : theme.space.APP_PADDING_X + 46}px;
    width: 340px;
    @media screen and (max-width: ${theme.breakpoints[1]}) {
      width: auto;
    }
    @media screen and (max-width: ${theme.breakpoints[0]}) {
      right: ${hasQRScan && theme.space.APP_PADDING_X + 86}px;
      left: ${theme.space.APP_PADDING_X + 46}px;
      width: auto;
    }
  `}
`;

const GuidedPracticeButton = styled(GoToGuidedPracticeButton)`
  ${({ theme }) => css`
    position: absolute;
    left: ${theme.space.APP_PADDING_X + 46}px;
    display: flex;
    @media screen and (max-width: ${theme.breakpoints[0]}) {
      display: none;
    }
  `}
`;

/**
 * This menu file is a my course navbar component
 */
class MainNav extends Component {
  constructor(props) {
    super(props);
    this.navContainer = React.createRef();
    this.state = {
      navContainer: null,
      hasBackground: false,
    };
  }

  componentDidMount() {
    this.setState({
      navContainer: this.navContainer,
    });
  }

  handleLogInClick = (values) => {
    // 'values' consists of userName = string, password = string, rememberData = boolean.
    const loginData = {
      username: values.userName,
      password: values.password,
    };
    this.props.login(loginData);
  };
  handleRegisterClick = () => {
    const { registerURL } = this.props.appConfig.skooldioLogin || {};
    if (registerURL) {
      this.props.onLogAction(genUserEvent(USER_ACTIONS.REGISTER, 'Login Menu'));
      setTimeout(() => {
        window.location = registerURL;
      }, 10);
    } else {
      this.props.onLogAction(genUserEvent(USER_ACTIONS.REGISTER, 'Login Menu'));
      this.props.history.push('/register');
    }
  };

  handleUserInfoClick = () => {
    // this.props.history.push('/profile');
  };

  handlePassportLoginClick = (loginURL) => {
    this.props.onLogAction(genUserEvent(USER_ACTIONS.LOGIN, 'Login Menu'));
    setTimeout(() => {
      window.location = loginURL;
    }, 10);
  };

  mapMenuItem = (items, menuItems = []) => {
    const {
      capacitorContext,
      appConfig: { isOpenExtLink, uiVariant, native },
    } = this.props;
    const { isNative, openUrl } = capacitorContext;
    const classroomProviderData = getClassroomProviderData(this.props.providerName);
    const { externalLinks, hasExternalLogout, externalLogoutUrl } = classroomProviderData ?? {};
    const { enablePushNotification, enablePersonalizedNotification } = native ?? {};

    const createExternalMenuItem = (externalItem) => {
      const {
        name,
        title,
        link,
        isLogGA,
        isFetch,
        params,
        isFetchWithAccessToken,
        isFetchWithPassportToken,
      } = externalItem;
      return {
        name: name,
        text: title,
        closeMenuOnClick: true,
        onClick: async () => {
          if (isLogGA) {
            this.props.onLogAction(
              genUserEvent(`${USER_ACTIONS.EXTERNAL_LINK} ${title}`, `${title} Menu`)
            );
          }
          let token = undefined;

          if (isFetchWithAccessToken) {
            token = await fetch2(
              urljoin(this.props.appConfig.userApi, '/me/session'),
              {},
              METHOD.POST,
              null,
              {
                isRedirect: false,
                includeCredentials: true,
              }
            );
          }

          if (isFetchWithPassportToken) {
            token = await fetch2(
              urljoin(
                this.props.appConfig.userApi,
                `/me/externalToken?providerName=${this.props.providerName}`
              ),
              null,
              METHOD.GET,
              null,
              {
                isRedirect: false,
                includeCredentials: true,
              }
            );
          }

          if (isFetch) {
            if (isNative) {
              const redirectToken = await fetch2(
                link,
                {},
                METHOD.GET,
                null,
                {
                  parseJson: false,
                },
                {},
                undefined,
                token
              );
              if (redirectToken) {
                const { redirect_uri: redirectUri } = params;
                const redirectUrl = `${redirectUri}?token=${redirectToken}`;
                openUrl(redirectUrl, () => window.open(redirectUrl, '_blank'));
              }
            } else {
              await fetch2(
                link,
                params,
                METHOD.GET,
                null,
                {
                  isRedirect: true,
                },
                {},
                undefined,
                token
              );
            }
          } else {
            const fullURL = token ? link.replace('{token}', token) : link;
            openUrl(fullURL, () => window.open(fullURL, '_blank'));
          }
        },
      };
    };

    if (externalLinks) {
      externalLinks.forEach((externalItem) => {
        if (isOpenExtLink) {
          menuItems.push(createExternalMenuItem(externalItem));
        }
      });
    }

    if (uiVariant !== 'PAAS') {
      const createFeedbackMenuItem = () => ({
        name: 'report',
        text: 'รายงานปัญหา',
        renderCustomMenuItem: () => {
          return (
            <AppFeedbackReport
              toggleComponent={
                <Flex alignItems={'center'} width={'100%'} height={'100%'}>
                  <Text textStyle={'heading5'} fontWeight={'bold'} pr={3}>
                    รายงานปัญหา
                  </Text>
                </Flex>
              }
              user={{ ...this.props.user, provider: this.props.providerName ?? 'unknown' }}
            />
          );
        },
      });
      menuItems.push(createFeedbackMenuItem());
    }

    const menuResult = !this.props.isLogin
      ? []
      : [
          ...menuItems,
          {
            name: 'logout',
            text: 'ออกจากระบบ',
            closeMenuOnClick: true,
            onClick: async () => {
              if (enablePushNotification && enablePersonalizedNotification) {
                try {
                  const { token } = await FCMPlugin.getToken();
                  if (token) {
                    const storeApolloClient = getStoreApolloClient();
                    await storeApolloClient.mutate({
                      mutation: nsUnregisterFcmToken,
                      variables: { token },
                    });
                    log.debug('FCMToken unregistered successfully');
                  }
                } catch (error) {
                  log.error('FCMToken unregistered failed: ', error);
                }
              }
              if (hasExternalLogout) {
                openUrl(externalLogoutUrl, () => {
                  window.location = externalLogoutUrl;
                });
              } else {
                await this.props.logout();
              }
            },
            rightAdornment: (
              <SvgIcon
                icon={SvgIcon.ICON_LIST.LOG_OUT}
                size={24}
                color={'inherit'}
                style={{ marginLeft: 'auto' }}
              />
            ),
          },
        ];
    return menuResult.filter((menu) => menu);
  };

  handleBackgroundChange = (background) => {
    this.setState({ hasBackground: background });
  };

  render() {
    const {
      searchTerm,
      resetSearchTerm,
      hasMenuBackground,
      rendermenus,
      loginError,
      clearLoginError,
      isLogin,
      hideMenu,
      match,
      theme: { space, colors },
      appConfig: {
        global: globalConfig,
        enableQRScan: enableQRScanConfig,
        myCourses: myCoursesConfig,
        guidedPractice: guidedPracticeConfig,
      },
    } = this.props;
    const { hasBackground } = this.state;
    const { disableNavbar } = globalConfig;
    const currentRoute = rendermenus.find((menu) => menu.path === match.path) || {};
    const { MyCourseTitle } = getAppUIVariantConfig();

    const enableSearchMyCourse = !!myCoursesConfig?.enableSearchMyCourse;

    const enableGuidedPracticeButton = !!guidedPracticeConfig?.enableGuidedPracticeButton;
    // NOTE: `isGuidedPracticeButtonEnabled` is available from version 2.51.0 and above
    const isGuidedPracticeButtonEnabled = enableGuidedPracticeButton;

    const isNative = CapacitorUtils.isNative;
    const hasSearchText = searchTerm !== '';
    const enableQRScan = isNative && enableQRScanConfig;
    const enableBackToHomePageButton = !isNative && enableSearchMyCourse && hasSearchText;
    const enableMyCourseTitle = hasBackground && !enableSearchMyCourse;

    if (disableNavbar) {
      return null;
    }

    return (
      <MenuContainer
        forwardedRef={this.navContainer}
        onBackgroundChange={this.handleBackgroundChange}
        hasMenuBackground={hasMenuBackground}
      >
        <Flex
          justifyContent="center"
          alignItems="center"
          px={'APP_PADDING_X'}
          style={{ zIndex: '0', position: 'relative', height: '100%' }}
        >
          {!hideMenu && (
            <NavBox
              display={['none', 'none', 'flex', 'flex']}
              justifyContent="flex-end"
              alignItems="center"
              flex="1 1 auto"
              px={'lg'}
              style={{ height: '100%' }}
            >
              {rendermenus.map((menu, i) => (
                <Box
                  mr={i !== rendermenus.length - 1 ? 4 : 0}
                  key={menu.path}
                  px={3}
                  style={{ height: '100%' }}
                >
                  <NavItem
                    exact={menu.path === '/'}
                    to={menu.path}
                    activeStyle={{
                      color: colors.white,
                      borderBottom: `3px solid ${colors.white}`,
                    }}
                  >
                    {menu.title}
                  </NavItem>
                </Box>
              ))}
            </NavBox>
          )}
          {enableMyCourseTitle && (
            <Text textStyle={'subtitle1'} color={MyCourseTitle === 'Dark' ? 'text' : 'white'}>
              {currentRoute.title}
            </Text>
          )}
          <Box style={{ position: 'absolute', left: space.APP_PADDING_X }}>
            {enableBackToHomePageButton ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                height="36px"
                width="36px"
                bg="rgba(59, 59, 59, 0.18)"
                style={{ borderRadius: '50%', cursor: 'pointer' }}
              >
                <SvgIcon
                  icon={SvgIcon.ICON_LIST.ARROW_LEFT}
                  size={24}
                  color="white"
                  onClick={() => {
                    resetSearchTerm();
                  }}
                />
              </Flex>
            ) : (
              <UserButton
                height={space.NAVBAR_HEIGHT}
                isLogin={isLogin}
                logInError={loginError && true}
                menuItems={this.mapMenuItem(rendermenus)}
                menuContainer={this.navContainer.current}
                onMenuItemClick={(e, index, item) =>
                  typeof item?.onClick === 'function' && item.onClick()
                }
                onClickUserInfo={this.handleUserInfoClick}
                onClickLogIn={this.handleLogInClick}
                onClickRegister={this.handleRegisterClick}
                onClickPassportLogin={this.handlePassportLoginClick}
                onOpenLoginModal={clearLoginError}
                dropdownIcon={{ iconName: 'licn-dropdown', size: '21px' }}
                hamburgerIcon={{ iconName: 'licn-hamburger', size: '21px' }}
                closeIcon={{ iconName: 'licn-close', size: '30px' }}
                checkboxIcon={{ iconName: 'licn-checked', size: '20px' }}
              />
            )}
          </Box>
          {isGuidedPracticeButtonEnabled && <GuidedPracticeButton />}
          {enableSearchMyCourse && (
            <BoxSearch hasQRScan={enableQRScan}>
              <SearchMyCourse />
            </BoxSearch>
          )}
          <Box style={{ position: 'absolute', right: space.APP_PADDING_X }}>
            <Notification height={space.NAVBAR_HEIGHT} iconProps={{ bg: colors.notificationBg }} />
          </Box>
          {enableQRScan && (
            <Box style={{ position: 'absolute', right: space.APP_PADDING_X + 46 }}>
              <QRScanner height={space.NAVBAR_HEIGHT} />
            </Box>
          )}
        </Flex>
      </MenuContainer>
    );
  }
}

MainNav.propTypes = {
  searchTerm: PropTypes.string,
  resetSearchTerm: PropTypes.func,
  hasMenuBackground: PropTypes.bool,
  rendermenus: PropTypes.array,
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.string,
      roles: PropTypes.array,
      private: PropTypes.bool,
    })
  ),
};

export default compose(withTheme, withAnalytic, withCapacitor, withConfig)(MainNav);
